import { Currency } from 'models/Currency';
import { CurrencyConvention } from 'models/CurrencyConvention';
import { HedgingInstrument } from 'models/HedgingInstrument';
import { IndexList } from 'models/IndexList';
import { Leg } from 'models/Leg';
import { LegFlow } from 'models/LegFlow';
import {
  RelationshipType,
  TransactionRelationship,
} from 'models/TransactionRelationship';
import { createAction, createAsyncAction } from 'typesafe-actions';

import { LegTerms } from '../../../../models/LegTerms';
import { Metric } from '../../../../models/Metric';
import { PayReceiveTypes } from '../../../../models/PayAcceptance/enums';
import { Valuation } from '../../../../models/Valuation';

import { Transaction } from '../../../RunDeliverable/store/types';

import { ReviewTradeModes } from '../../components/ButtonActions/components/ApproveTradeForm/enum';
import { FiledDownloadItem } from '../../components/ButtonActions/components/UpdateTrade/components/UploadFiles/types';
import { LegCardsType } from '../../components/LegsContainer/components/CopyPasteLegFlowsForm/types';
import {
  FxRateLockerModes,
  PricerModes,
  TransactionReviewStatusModes,
  TransactionStatusModes,
} from '../../enums';

import { FlowsState } from './components/FlowsReducer';
import { TradeSummaryState } from './components/TradeSummaryReducer';
import {
  ApprovalRequest,
  ExecutionCriteria,
  FxAveragingOptions,
  FxOptions,
} from './types';

export type SetHedgingInstrumentPayload = {
  instrument_type_id: HedgingInstrument['instrument_id'];
};

export const setHedgingInstrument = createAction(
  'pricer/setHedgingInstrument',
)<SetHedgingInstrumentPayload>();

export type GetTransactionByIdRequestPayload = {
  transactionId: Transaction['transaction_id'];
};
export type GetTransactionByIdSuccessPayload = {
  data: {
    transaction_id: number;
    trade_summary: TradeSummaryState;
    legs: Array<{
      leg_terms: LegTerms;
      leg_flows: FlowsState;
    }>;
    pricerType: PricerModes;
    approval_request: null | ApprovalRequest;
    option?: FxOptions | null;
    fx_averaging?: FxAveragingOptions | null;
    execution_criteria?: ExecutionCriteria | null;
  };
};

export const getTransactionById = createAsyncAction(
  'pricer/getTransactionById/request',
  'pricer/getTransactionById/success',
  'pricer/getTransactionById/failure',
)<
  GetTransactionByIdRequestPayload,
  GetTransactionByIdSuccessPayload,
  undefined
>();

export type SetPricerTypePayload = {
  pricerType: PricerModes;
};

export const setPricerType = createAction(
  'pricer/setPricerType',
)<SetPricerTypePayload>();

export type PopulateTradeDefaultPayload = {
  instrument_type_id: HedgingInstrument['instrument_id'];
  market_date: string;
  user_currency_id: Currency['currency_id'];
  ir_indices: IndexList[];
  currencies: Currency[];
  currency_conventions: CurrencyConvention[];
  calculation_calendar: Currency['default_calendar_pmca_id'] | Array<number>;
};

export const populateTradeDefault = createAction(
  'pricer/populateTradeDefault',
)<PopulateTradeDefaultPayload>();

export type BuildSchedulePayloadRequest = {
  legs: Leg[];
};
export type BuildSchedulePayloadSuccess = {
  flows: LegFlow[][];
};

export const buildSchedule = createAsyncAction(
  'pricer/buildSchedule/request',
  'pricer/buildSchedule/success',
  'pricer/buildSchedule/failure',
)<BuildSchedulePayloadRequest, BuildSchedulePayloadSuccess, undefined>();

export type SaveTradePayloadRequest = {
  data: {
    curve_date: string;
    valuation_date: string;
    transaction: {
      transaction_id: number | null;
      trade_summary: TradeSummaryState;
      legs: Leg[];
      option?: FxOptions;
      fx_averaging?: FxAveragingOptions | null;
      execution_criteria?: ExecutionCriteria;
      update_trade?: boolean;
      is_amendment?: boolean;
      percentage: number | null;
    };
  };
  stopNavigationToNewPage?: boolean;
};
export type SaveTradePayloadSuccess = {
  transaction_id: number;
};

export const saveTrade = createAsyncAction(
  'pricer/saveTrade/request',
  'pricer/saveTrade/success',
  'pricer/saveTrade/failure',
)<SaveTradePayloadRequest, SaveTradePayloadSuccess, undefined>();

export const saveCopiedTrade = createAsyncAction(
  'pricer/saveCopiedTrade/request',
  'pricer/saveCopiedTrade/success',
  'pricer/saveCopiedTrade/failure',
)<SaveTradePayloadRequest, SaveTradePayloadSuccess, undefined>();

export type UpdateTypeTradePayloadSuccess = {
  transaction_id: number;
  trade_summary: TradeSummaryState;
  legs: Array<{
    leg_terms: LegTerms;
    leg_flows: FlowsState;
  }>;
  pricerType: PricerModes;
  approval_request: ApprovalRequest;
};

export const updateTypeTrade = createAsyncAction(
  'pricer/updateTypeTrade/request',
  'pricer/updateTypeTrade/success',
  'pricer/updateTypeTrade/failure',
)<SaveTradePayloadRequest, UpdateTypeTradePayloadSuccess, undefined>();

export type UpdateDeletePayloadRequest = {
  transaction_id: number;
};

export const deleteTrade = createAsyncAction(
  'pricer/deleteTrade/request',
  'pricer/deleteTrade/success',
  'pricer/deleteTrade/failure',
)<UpdateDeletePayloadRequest, UpdateTypeTradePayloadSuccess, undefined>();

export type ValueTradePayloadRequest = {
  data: {
    curve_date: string;
    valuation_date: string;
    transaction: {
      transaction_id: number | null;
      trade_summary: TradeSummaryState;
      legs: Leg[];
      option?: FxOptions;
      fx_averaging?: FxAveragingOptions | null;
      execution_criteria?: ExecutionCriteria;
      update_trade?: boolean;
      is_amendment?: boolean;
      percentage: number | null;
    };
  };
};
export type ValueTradePayloadSuccess = {
  data: {
    flows: LegFlow[][];
    metrics: Metric[];
    valuation: Valuation[];
    transaction_id: number;
    // Swaption-only: the (surface) volatility the backend priced with.
    swaption_volatility?: number | null;
  };
};

export const valueTrade = createAsyncAction(
  'pricer/valueTrade/request',
  'pricer/valueTrade/success',
  'pricer/valueTrade/failure',
)<ValueTradePayloadRequest, ValueTradePayloadSuccess, undefined>();

export type DeleteLegPayload = {
  legNumber: string | number;
};
export const deleteLeg = createAction('pricer/deleteLeg')<DeleteLegPayload>();

export type DeleteLegFlowsPayload = {
  legNumber: string | number;
};
export const deleteLegFlows = createAction(
  'pricer/deleteLegFlows',
)<DeleteLegFlowsPayload>();

export type UpdateTradeSummaryStateFromFormPayload = {
  data: Partial<TradeSummaryState>;
};
export const updateTradeSummaryStateFromForm = createAction(
  'pricer/displaySettings/updateTradeSummaryStateFromForm',
)<UpdateTradeSummaryStateFromFormPayload>();

export type GetFilesListDownloadRequestPayload = {
  transaction_id: number;
};
export type GetFilesListDownloadSuccessPayload = {
  data: FiledDownloadItem[];
  transaction_id: number;
};
export const getFilesListDownload = createAsyncAction(
  'pricer/getFilesListDownload/request',
  'pricer/getFilesListDownload/success',
  'pricer/getFilesListDownload/failure',
)<
  GetFilesListDownloadRequestPayload,
  GetFilesListDownloadSuccessPayload,
  GetFilesListDownloadRequestPayload
>();

export type ReviewTradeRequestPayload = {
  data: { message: string; transaction_ids: number[] };
  type: ReviewTradeModes;
};
export type ReviewTradeSuccessPayload = {
  transactions: {
    review_date: string;
    reviewer_id: string;
    transaction_id: number;
    transaction_review_status: keyof typeof TransactionReviewStatusModes;
    transaction_status: TransactionStatusModes;
    transaction_type_id: number;
    comments: string;
  }[];
};
export const reviewTrade = createAsyncAction(
  'pricer/reviewTrade/request',
  'pricer/reviewTrade/success',
  'pricer/reviewTrade/failure',
)<ReviewTradeRequestPayload, ReviewTradeSuccessPayload, undefined>();

export type CopyPasteLegTermsFlowsPayload = {
  copiedLegNumber: number | string;
  pastedLegNumbers: LegCardsType;
};
export const copyPasteLegTerms = createAction(
  'pricer/copyPasteLegTerms/request',
)<CopyPasteLegTermsFlowsPayload>();

export const copyPasteLegFlows = createAction(
  'pricer/copyPasteLegFlows/request',
)<CopyPasteLegTermsFlowsPayload>();

export type GetFxSpotRateRequestPayload = {
  left_currency_id: Currency['currency_id'];
  right_currency_id: Currency['currency_id'];
  curve_date: string;
};
export type GetFxSpotRateSuccessPayload = {
  data: number;
};
export const getFxSpotRate = createAsyncAction(
  'pricer/getFxSpotRate/request',
  'pricer/getFxSpotRate/success',
  'pricer/getFxSpotRate/failure',
)<GetFxSpotRateRequestPayload, GetFxSpotRateSuccessPayload, undefined>();

export type GetFxForwardRateRequestPayload = {
  left_currency_id: Currency['currency_id'];
  right_currency_id: Currency['currency_id'];
  curve_date: string;
  settlement_date: string;
};
export type GetFxForwardRateSuccessPayload = {
  data: number;
};
export const getFxForwardRate = createAsyncAction(
  'pricer/getFxForwardRate/request',
  'pricer/getFxForwardRate/success',
  'pricer/getFxForwardRate/failure',
)<GetFxForwardRateRequestPayload, GetFxForwardRateSuccessPayload, undefined>();

export type ChangeFxLockerPayload = {
  lockerValue: FxRateLockerModes;
};
export const changeFxLocker = createAction(
  'pricer/changeFxLocker/request',
)<ChangeFxLockerPayload>();

export type UpdateFxRateLegNotionalPayload = {
  leftLegNotional: number;
  rightLegNotional: number;
  fxRate: number;
};
export const updateFxRateLegNotional = createAction(
  'pricer/updateFxRateLegNotional/request',
)<UpdateFxRateLegNotionalPayload>();

export type SetFxOptionPayload = {
  maturity_date: string | null;
  volatility: number | null;
  pay_receive_id: PayReceiveTypes | null;
  has_option: boolean;
};

export const setFxOption =
  createAction('pricer/setFxOption')<SetFxOptionPayload>();

export type SetFxAveragingOptionPayload = {
  average_start_date: null | string;
  average_end_date: null | string;
};

export const setFxAveragingOption = createAction(
  'pricer/setFxAveragingOption',
)<SetFxAveragingOptionPayload>();

export type SetExecutionCriteriaPayload = {
  spot_reference: number | null;
  mid_forward_points: number | null;
  total_charges: number | null;
};

export const setExecutionCriteria = createAction(
  'pricer/setExecutionCriteria',
)<SetExecutionCriteriaPayload>();

export const resetAndChangeInstrumentType = createAction(
  'pricer/resetAndChangeInstrumentType',
)();

export const changePricerTypeReset = createAction(
  'pricer/changePricerTypeReset',
)();

export type ChangePercentagePayload = {
  percentage: number | null;
};

export const changePercentage = createAction(
  'pricer/changePercentage',
)<ChangePercentagePayload>();

export type ReorderLegsRequestPayload = {
  leg_order: string[];
  transaction_id: number;
};

export const reorderLegs = createAsyncAction(
  'pricer/reorderLegs/request',
  'pricer/reorderLegs/success',
  'pricer/reorderLegs/failure',
)<ReorderLegsRequestPayload, undefined, undefined>();

export type GetTransactionRelationshipsByTradeRequestPayload = {
  transactionId: number;
};
export type GetTransactionRelationshipsByTradeSuccessPayload = {
  data: TransactionRelationship[];
};

export const getTransactionRelationshipsByTrade = createAsyncAction(
  'pricer/getTransactionRelationshipsByTrade/request',
  'pricer/getTransactionRelationshipsByTrade/success',
  'pricer/getTransactionRelationshipsByTrade/failure',
)<
  GetTransactionRelationshipsByTradeRequestPayload,
  GetTransactionRelationshipsByTradeSuccessPayload,
  undefined
>();

export type SaveTransactionRelationshipRequestPayload = {
  data: {
    parent_transaction_id: number;
    child_transaction_id: number;
    relationship_type_id: number;
    actual_ineffective_initial_value: number;
    hypo_ineffective_initial_value: number;
    transaction_relationship_id?: number;
  };
  transactionId: number;
  tableFormModalId: string;
  transaction_relationship_id?: number;
};

export const saveTransactionRelationship = createAsyncAction(
  'pricer/saveTransactionRelationship/request',
  'pricer/saveTransactionRelationship/success',
  'pricer/saveTransactionRelationship/failure',
)<SaveTransactionRelationshipRequestPayload, undefined, undefined>();

export type DeleteTransactionRelationshipRequestPayload = {
  data: { transactionRelationshipId: number };
  transactionId: number;
  tableFormModalId: string;
};

export const deleteTransactionRelationship = createAsyncAction(
  'pricer/deleteTransactionRelationship/request',
  'pricer/deleteTransactionRelationship/success',
  'pricer/deleteTransactionRelationship/failure',
)<DeleteTransactionRelationshipRequestPayload, undefined, undefined>();

export type GetTransactionRelationshipTypesSuccessPayload = {
  data: RelationshipType[];
};

export const getTransactionRelationshipTypes = createAsyncAction(
  'pricer/getTransactionRelationshipTypes/request',
  'pricer/getTransactionRelationshipTypes/success',
  'pricer/getTransactionRelationshipTypes/failure',
)<undefined, GetTransactionRelationshipTypesSuccessPayload, undefined>();

export type SaveRelationshipTypeRequestPayload = {
  data: {
    description_forward: string;
    description_backwards: string;
  };
  tableFormModalId: string;
  relationship_type_id?: number;
};

export const saveTransactionRelationshipType = createAsyncAction(
  'pricer/saveTransactionRelationshipType/request',
  'pricer/saveTransactionRelationshipType/success',
  'pricer/saveTransactionRelationshipType/failure',
)<SaveRelationshipTypeRequestPayload, undefined, undefined>();

export type DeleteTransactionRelationshipTypeRequestPayload = {
  data: { relationship_type_id: number };
  tableFormModalId: string;
};

export const deleteTransactionRelationshipType = createAsyncAction(
  'pricer/deleteTransactionRelationshipType/request',
  'pricer/deleteTransactionRelationshipType/success',
  'pricer/deleteTransactionRelationshipType/failure',
)<DeleteTransactionRelationshipTypeRequestPayload, undefined, undefined>();

export const reset = createAction('pricer/reset')();
